import Toast from '@common/helpers/toast';
import { UserTreatment } from '@/models/user-treatment';
import ApiHelper from '@/api/ApiHelper';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';

// interfaces

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 20;
const DEFAULT_LAST_PAGE = 1;

export interface SetUserTreatmentHandlerTypePayload {
  id: number;
  handler_type: string;
}

@Module({ name: 'user_treatment_list', namespaced: true })
export default class Index extends VuexModule<any> {
  private data: UserTreatment[] | [] = [];

  private isLoading: boolean = false;
  private perPage: number = DEFAULT_PER_PAGE;
  private page: number = DEFAULT_PAGE;
  private lastPage: number = DEFAULT_LAST_PAGE;

  // ********* Mutations ********* //
  @Mutation
  public setUserTreatmentList(userTreatmentList: UserTreatment[] | []) {
    this.data = userTreatmentList;
  }

  @Mutation
  public syncUnreadCount(data: { reservationId: number; unreadMessageCount: number }) {
    this.data.forEach((item) => {
      const hasReservation = item.reservations?.some((r) => r.id === data.reservationId);
      if (hasReservation) {
        item.unread_message = data.unreadMessageCount;
      }
    });
  }

  // feedback_type is intentionally left untouched when the caller doesn't
  // pass it, so partial payloads (e.g. only updating state) don't silently
  // wipe it back to undefined.
  @Mutation
  public setUserTreatmentState(data: { id: number; state: string; feedback_type?: string }) {
    const userTreatment = this.data.find((item) => item.id === data.id);
    if (!userTreatment) {
      return;
    }

    userTreatment.state = data.state;
    if (data.feedback_type !== undefined) {
      userTreatment.feedback_type = data.feedback_type;
    }
  }

  // Sets the new state/plan and clears the stale questionnaire answer date
  // (問診回答日) in one step, so callers don't need several separate commits.
  @Mutation
  public applyPlanChanged({ id, state, plan, reMonshin }: { id: number; state: string; plan?: any; reMonshin?: boolean }) {
    const userTreatment = this.data.find((item) => item.id === id);
    if (!userTreatment) {
      return;
    }

    userTreatment.state = state;
    if (plan) {
      userTreatment.plan = plan;
    }
    // Only clear latest_answer when re_monshin is explicitly true.
    // `reMonshin !== false` was a latent trap: undefined !== false is truthy,
    // so omitting the field from a call site would silently wipe latest_answer
    // even on a plain plan-change that has nothing to do with re-questionnaire.
    if (reMonshin === true) {
      userTreatment.reservations?.forEach((reservation) => {
        reservation.latest_answer = undefined;
      });
    }
  }

  @Mutation
  public setUserTreatmentHandlerType(payload: SetUserTreatmentHandlerTypePayload) {
    const userTreatment = this.data.find((item) => item.id === payload.id);
    if (userTreatment) {
      userTreatment.handler_type = payload.handler_type;
    }
  }

  @Mutation
  public setUserTreatmentLatestAnswer(data) {
    const finalReservation = data.final_reservation || data.reservations[0];
    const finalReservationId = finalReservation?.id;

    for (const userTreatment of this.data) {
      if (userTreatment.id !== data.id) {
        continue;
      }

      if (userTreatment.reservations) {
        for (const reservation of userTreatment.reservations) {
          if (reservation.id === finalReservationId) {
            reservation.latest_answer = { ...finalReservation?.latest_answer };
            break;
          }
        }
      }

      userTreatment.state = data.state;
      userTreatment.feedback_type = data.feedback_type;
      break;
    }
  }

  @Mutation
  public setDataUserTreatmentListPaginate(response: any) {
    // this.data.push(...response.data);
    this.perPage = response.per_page;
    this.page = response.current_page;
    this.lastPage = response.last_page;
  }

  @Mutation
  public resetUserTreatmentListState() {
    this.data = [];
    this.isLoading = false;
    this.perPage = DEFAULT_PER_PAGE;
    this.page = DEFAULT_PAGE;
    this.lastPage = DEFAULT_LAST_PAGE;
  }

  @Mutation
  public setUserTreatmentListLoading(isLoading = true) {
    this.isLoading = isLoading;
  }

  // ********* Actions ********* //
  @Action({ rawError: true })
  public async getUserTreatmentList(params) {
    const { commit, state } = this.context;
    try {
      commit('setUserTreatmentListLoading');
      const {payload, $state} = params;
      payload.per_page = state.perPage;
      payload.page = state.page;

      const response = await ApiHelper.getApi('UserTreatmentV2Api').index( payload);
      const { data } = response;
      this.setUserTreatmentList(data);

      response.current_page += 1;
      commit('setDataUserTreatmentListPaginate', response);

      // when page > last page -> stop load more.
      state.page <= response.last_page ? $state.loaded() : $state.complete();
    } catch (error) {
      const data = error.response.data;
      Toast.error(data.message);
    } finally {
      commit('setUserTreatmentListLoading', false);
    }
  }
}
